import { useEffect, useState, type FormEvent } from "react";
import { Api, ApiError } from "../sync/api";
import { Field, Form } from "./ui";
import { t } from "../i18n";
import { logo, useBranding } from "../brand";
import { LanguageSwitch } from "./LanguageSwitch";

type Info = { workspace: string; handle: string; display_name: string };

const api = new Api("/api/v1", null);

// PasswordLink is where a link to set a password leads, which an admin
// made and handed over: one sets a password, then signs in with it.
export function PasswordLink({ token, onDone }: { token: string; onDone: (handle: string) => void }) {
  const brand = useBranding();
  const [info, setInfo] = useState<Info | null>(null);
  const [bad, setBad] = useState(false);
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.request<{ link: Info }>("GET", `/password-links/${encodeURIComponent(token)}`).then(
      ({ link }) => setInfo(link),
      () => setBad(true),
    );
  }, [token]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (password !== again) return setError(t("The passwords are not the same."));
    setBusy(true);
    setError("");
    try {
      const r = await api.request<{ handle: string }>("POST", `/password-links/${encodeURIComponent(token)}`, { password });
      onDone(r.handle);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("Could not reach the server."));
      setBusy(false);
    }
  }

  return (
    <main className="page login">
      <div className="card login-card">
        <img className="login-logo" src={logo(192, brand)} alt="" width={44} height={44} />
        {bad ? (
          <>
            <h1>{t("This link does not work")}</h1>
            <p className="login-lede">{t("It may have expired, been used, or been replaced by a newer one. Ask an admin for a new link.")}</p>
            <a className="btn" href="/">
              {t("Go to sign in")}
            </a>
          </>
        ) : !info ? (
          <p className="login-lede">{t("Loading…")}</p>
        ) : (
          <>
            <h1>{t("Set your password")}</h1>
            <p className="login-lede">{t("For {name} (@{handle}) in {workspace}. You sign in with it next.", { name: info.display_name, handle: info.handle, workspace: info.workspace })}</p>
            <Form onSubmit={save}>
              <input type="text" value={info.handle} autoComplete="username" readOnly hidden />
              <Field label={t("Password")} hint={t("At least 12 characters.")}>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} autoComplete="new-password" autoFocus required />
              </Field>
              <Field label={t("Password again")}>
                <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} minLength={12} autoComplete="new-password" required />
              </Field>
              {error && (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              <button type="submit" disabled={busy}>
                {busy ? t("Saving…") : t("Set password")}
              </button>
            </Form>
          </>
        )}
      </div>
      <LanguageSwitch />
    </main>
  );
}
